export type CoreSkinId = 'neon' | 'solar' | 'void';
export type SkillSkinId = 'neon' | 'solar' | 'void';

export interface CosmeticLoadout {
  coreSkinId: CoreSkinId;
  skillSkinId: SkillSkinId;
}

export interface CoreSkinDefinition {
  id: CoreSkinId;
  name: string;
  subtitle: string;
  lore: string;
  primary: number;
  secondary: number;
  glow: number;
}

export interface SkillSkinDefinition {
  id: SkillSkinId;
  name: string;
  subtitle: string;
  description: string;
  kinetic: number;
  tesla: number;
  cryo: number;
  gravity: number;
}

export const DEFAULT_COSMETICS: CosmeticLoadout = {
  coreSkinId: 'neon',
  skillSkinId: 'neon',
};

export const CORE_SKINS: readonly CoreSkinDefinition[] = [
  {
    id: 'neon',
    name: 'Neon Vanguard',
    subtitle: 'Precision chassis',
    lore: 'A clean cyan reactor wrapped in lime targeting filaments.',
    primary: 0x00e5ff,
    secondary: 0x80ff24,
    glow: 0x8cf5ff,
  },
  {
    id: 'solar',
    name: 'Solar Crown',
    subtitle: 'Royal overdrive chassis',
    lore: 'Molten-gold armor that burns brighter with every ascension.',
    primary: 0xffb31f,
    secondary: 0xff5f2e,
    glow: 0xfff1a8,
  },
  {
    id: 'void',
    name: 'Void Ascendant',
    subtitle: 'Mythic singularity chassis',
    lore: 'A forbidden violet shell forged around a collapsed star.',
    primary: 0xc33cff,
    secondary: 0xff3dd1,
    glow: 0xe9b8ff,
  },
] as const;

export const SKILL_SKINS: readonly SkillSkinDefinition[] = [
  {
    id: 'neon',
    name: 'Neon Circuit',
    subtitle: 'Arcade clarity',
    description: 'Cyan arcs, cryogenic blue and reactor-lime impact trails.',
    kinetic: 0x80ff24,
    tesla: 0x00e5ff,
    cryo: 0x63efff,
    gravity: 0xc33cff,
  },
  {
    id: 'solar',
    name: 'Solar Relic',
    subtitle: 'Golden devastation',
    description: 'White-hot projectiles, amber lightning and crimson gravity.',
    kinetic: 0xffd321,
    tesla: 0xff9e2f,
    cryo: 0xfff0a4,
    gravity: 0xff4f63,
  },
  {
    id: 'void',
    name: 'Void Protocol',
    subtitle: 'Mythic corruption',
    description: 'Magenta ammunition, violet storms and spectral frost.',
    kinetic: 0xff3dd1,
    tesla: 0xa66cff,
    cryo: 0x79c7ff,
    gravity: 0x6d36ff,
  },
] as const;

export function isCoreSkinId(value: unknown): value is CoreSkinId {
  return value === 'neon' || value === 'solar' || value === 'void';
}

export function isSkillSkinId(value: unknown): value is SkillSkinId {
  return value === 'neon' || value === 'solar' || value === 'void';
}

export function getCoreSkin(id: CoreSkinId): CoreSkinDefinition {
  return CORE_SKINS.find((skin) => skin.id === id) ?? CORE_SKINS[0]!;
}

export function getSkillSkin(id: SkillSkinId): SkillSkinDefinition {
  return SKILL_SKINS.find((skin) => skin.id === id) ?? SKILL_SKINS[0]!;
}
